import React, { useEffect, useRef, useState } from 'react';

import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';

import SingleImageUploadField from '~/components/Common/Forms/SingleImageUploadField';
import type { SingleImageUploadFieldHandle } from '~/components/Common/Forms/SingleImageUploadField';
import type { FlatImageEditorPendingUploadState } from '~/components/Common/Images/types';

interface Props {
  isSaving?: boolean;
  logoId?: string;
  open: boolean;
  onClose: () => void;
  onSave: (logoId: string | null) => Promise<void>;
}

const EMPTY_UPLOAD_STATE: FlatImageEditorPendingUploadState = {
  isUploadingPending: false,
  pendingUploadCount: 0,
};

export default function LogoEditorDialog({ isSaving = false, logoId, open, onClose, onSave }: Props) {
  const logoFieldRef = useRef<SingleImageUploadFieldHandle | null>(null);
  const [draftLogoId, setDraftLogoId] = useState('');
  const [uploadState, setUploadState] = useState<FlatImageEditorPendingUploadState>(EMPTY_UPLOAD_STATE);
  const [errorMessage, setErrorMessage] = useState('');
  const isBusy = isSaving || uploadState.isUploadingPending;

  useEffect(() => {
    if (open) {
      setDraftLogoId(logoId ?? '');
      setUploadState(EMPTY_UPLOAD_STATE);
      setErrorMessage('');
    }
  }, [logoId, open]);

  const handleSave = async () => {
    setErrorMessage('');

    try {
      const uploadResult = await logoFieldRef.current?.uploadAllPending();

      if (uploadResult?.failedCount) {
        setErrorMessage('Logo upload failed. Try again before saving.');
        return;
      }

      const nextLogoId = logoFieldRef.current?.getValue() ?? draftLogoId;

      await onSave(nextLogoId || null);
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Failed to save logo.');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Replace logo</DialogTitle>
      <DialogContent>
        {errorMessage && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errorMessage}
          </Alert>
        )}
        <SingleImageUploadField
          ref={logoFieldRef}
          value={draftLogoId}
          disabled={isSaving}
          uploadHelperText="Crop and adjust the logo before uploading it."
          onChange={setDraftLogoId}
          onPendingUploadStateChange={setUploadState}
        />
      </DialogContent>
      <DialogActions>
        <Button variant="text" disabled={isBusy} onClick={onClose}>
          Cancel
        </Button>
        <Button variant="contained" disabled={isBusy} onClick={handleSave}>
          {isSaving ? 'Saving...' : 'Save logo'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
